import { useState } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { api, money } from "@/lib/api";

const Line = ({ item, setQty }) => (
  <li data-testid={`cart-item-${item.lookup_key}`} className="flex gap-4 py-5 border-b border-espresso/10">
    <img src={item.image} alt={item.name} className="w-20 h-24 object-cover bg-[#F1EDE4]" />
    <div className="flex-1 min-w-0">
      <div className="flex justify-between gap-2">
        <p className="font-display text-xl leading-tight">{item.name}</p>
        <button data-testid={`cart-remove-${item.lookup_key}`} onClick={() => setQty(item.id, 0)} aria-label="Remove" className="text-mocha hover:text-espresso">
          <X className="w-4 h-4" strokeWidth={1.4} />
        </button>
      </div>
      <p className="text-xs text-mocha mt-1">{[item.option, item.flavor].filter(Boolean).join(" · ")}</p>
      <div className="flex items-center justify-between mt-3">
        <div className="flex items-center border border-espresso/20">
          <button data-testid={`cart-dec-${item.lookup_key}`} onClick={() => setQty(item.id, item.qty - 1)} className="p-2" aria-label="Decrease"><Minus className="w-3 h-3" /></button>
          <span className="w-8 text-center text-sm">{item.qty}</span>
          <button data-testid={`cart-inc-${item.lookup_key}`} onClick={() => setQty(item.id, Math.min(100, item.qty + 1))} className="p-2" aria-label="Increase"><Plus className="w-3 h-3" /></button>
        </div>
        <span className="text-sm">{money(item.price * item.qty)}</span>
      </div>
    </div>
  </li>
);

export const CartDrawer = () => {
  const { items, setQty, subtotal, open, setOpen } = useCart();
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ lookup_key: i.lookup_key, quantity: i.qty, flavor: i.mix ? "" : i.flavor || "", mix: i.mix || null })),
        origin_url: window.location.origin,
        note,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout is unavailable right now. Please try again shortly.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent data-testid="cart-drawer" data-lenis-prevent className="bg-cream w-full sm:max-w-md flex flex-col p-0 border-l border-espresso/10">
        <div className="px-7 pt-8 pb-5 border-b border-espresso/10">
          <SheetTitle className="font-display text-3xl font-normal text-espresso">Your Bag</SheetTitle>
          <SheetDescription className="text-mocha text-sm">Pickup in Clifton Heights, PA</SheetDescription>
        </div>
        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-start justify-center px-7">
            <p className="font-display text-2xl italic text-mocha">Your bag is waiting for something sweet.</p>
            <Link to="/shop" onClick={() => setOpen(false)} data-testid="cart-empty-shop" className="btn btn-line mt-8">Browse the Shop</Link>
          </div>
        ) : (
          <>
            <ul className="flex-1 overflow-y-auto px-7" data-lenis-prevent>
              {items.map((i) => <Line key={i.id} item={i} setQty={setQty} />)}
            </ul>
            <div className="px-7 py-6 border-t border-espresso/10 space-y-4">
              <textarea
                data-testid="cart-note"
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 400))}
                placeholder="Order note (preferred pickup date, gift message…)"
                rows={2}
                className="w-full bg-transparent border border-espresso/20 p-3 text-sm placeholder:text-mocha/80 focus:outline-none focus:border-gold"
              />
              <div className="flex justify-between items-baseline">
                <span className="eyebrow">Subtotal</span>
                <span data-testid="cart-subtotal" className="font-display text-3xl">{money(subtotal)}</span>
              </div>
              <p className="text-xs text-mocha">Taxes calculated at checkout. Pickup details are sent once payment is received.</p>
              <button data-testid="checkout-button" onClick={checkout} disabled={loading} className="btn btn-dark w-full">
                {loading ? "Preparing checkout…" : "Checkout"}
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
